import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { TKeyValue } from "../../../utils/commonInterfaces";
import FieldGridContainer from "../fieldGridContainer";

function keysMatch(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return String(a) === String(b);
}

/** MUI Select can throw if `value` does not match any MenuItem (e.g. options still loading). */
function singleSelectDisplayValue(
  fieldValue: unknown,
  items: TKeyValue<string | number>[],
): string {
  if (!items.length) return "";
  const hit = items.find(
    (item) =>
      item != null && item.key != null && keysMatch(item.key, fieldValue),
  );
  return hit ? String(hit.key) : "";
}

export type ControlledSelectFieldProps<T extends FieldValues = FieldValues> = {
  label: string;
  id: string;
  name: Path<T>;
  value?: string | string[];
  defaultValue?: string | string[];
  onChangeHandler?: (event: SelectChangeEvent) => void;
  itemList: TKeyValue<string | number>[];
  isRequired?: boolean;
  helperText?: string;
  control?: Control<T>;
  isGridItem?: boolean;
  errorMessage?: string;
  multiple?: boolean;
};

/** RHF Control<T> is invariant; bridge internally for callers. */
function ControlledSelectField<T extends FieldValues = FieldValues>(
  props: ControlledSelectFieldProps<T>,
) {
  const {
    label,
    id,
    name,
    value = "",
    defaultValue = "",
    itemList = [],
    isRequired = false,
    helperText = "",
    control,
    isGridItem = true,
    errorMessage,
    multiple = false,
  } = props;

  const resolvedControl = control as unknown as Control<FieldValues>;
  const resolvedName = name as string;

  const renderValue = multiple
    ? (selected: unknown) => (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {(Array.isArray(selected) ? selected : []).map((code) => {
          const item = itemList.find((i) => keysMatch(i.key, code));
          return (
            <Chip key={String(code)} label={item?.value ?? String(code)} size="small" />
          );
        })}
      </Box>
    )
    : undefined;

  if (isGridItem) {
    return (
      <FieldGridContainer>
        {control ? (
          <Controller
            name={resolvedName}
            control={resolvedControl}
            render={({ field, fieldState }) => {
              const validationMessage =
                errorMessage ?? fieldState.error?.message;
              const singleDisplay = multiple
                ? ""
                : singleSelectDisplayValue(field.value, itemList);
              return (
                <FormControl
                  fullWidth
                  required={isRequired}
                  error={!!validationMessage}
                  key={String(defaultValue) + String(value)}
                >
                  <InputLabel id={id}>
                    {label}
                  </InputLabel>
                  <Select
                    {...field}
                    size="small"
                    labelId={id + "label"}
                    id={id + "field"}
                    label={label}
                    variant="outlined"
                    sx={{ marginTop: "0px !important" }}
                    value={
                      multiple ? (field.value ?? []) : singleDisplay
                    }
                    multiple={multiple}
                    renderValue={renderValue}
                    onChange={
                      multiple
                        ? field.onChange
                        : (e: SelectChangeEvent<string>) => {
                            const str = e.target.value;
                            const hit = itemList.find(
                              (item) =>
                                item != null &&
                                item.key != null &&
                                String(item.key) === str,
                            );
                            field.onChange(hit ? hit.key : str);
                          }
                    }
                  >
                    {!multiple ? (
                      <MenuItem value="" sx={{ display: "none" }} aria-hidden />
                    ) : null}
                    {itemList
                      .filter((item) => item != null && item.key != null)
                      .map((item) => {
                        return (
                          <MenuItem key={String(item.key)} value={String(item.key)}>
                            <ListItemText sx={{ textAlign: "left" }}>
                              {item.value}
                            </ListItemText>
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {helperText ? (
                    <FormHelperText>{helperText}</FormHelperText>
                  ) : null}
                  {validationMessage ? (
                    <Typography variant="caption" color="error">
                      {validationMessage}
                    </Typography>
                  ) : null}
                </FormControl>
              );
            }}
          />
        ) : (
          <FormControl
            fullWidth
            required={isRequired}
            key={String(defaultValue) + String(value)}
          >
            <InputLabel id={id}>
              {label}
            </InputLabel>
            <Select
              name={resolvedName}
              size="small"
              labelId={id + "label"}
              id={id + "field"}
              label={label}
              variant="outlined"
              sx={{ marginTop: "0px !important" }}
              defaultValue={defaultValue}
            >
              {itemList
                .filter((item) => item != null && item.key != null)
                .map((item) => {
                  return (
                    <MenuItem key={String(item.key)} value={String(item.key)}>
                      <ListItemText sx={{ textAlign: "left" }}>
                        {item.value}
                      </ListItemText>
                    </MenuItem>
                  );
                })}
            </Select>
            {helperText ? (
              <FormHelperText>{helperText}</FormHelperText>
            ) : null}
          </FormControl>
        )}
      </FieldGridContainer>
    );
  }

  return (
    <>
      {control ? (
        <Controller
          name={resolvedName}
          control={resolvedControl}
          render={({ field, fieldState }) => {
            const validationMessage =
              errorMessage ?? fieldState.error?.message;
            const singleDisplay = multiple
              ? ""
              : singleSelectDisplayValue(field.value, itemList);
            return (
              <FormControl
                fullWidth
                required={isRequired}
                error={!!validationMessage}
                key={String(defaultValue) + String(value)}
              >
                <InputLabel id={id}>
                  {label}
                </InputLabel>
                <Select
                  {...field}
                  size="small"
                  labelId={id + "label"}
                  id={id + "field"}
                  label={label}
                  variant="outlined"
                  sx={{ marginTop: "0px !important" }}
                  value={
                    multiple ? (field.value ?? []) : singleDisplay
                  }
                  multiple={multiple}
                  renderValue={renderValue}
                  onChange={
                    multiple
                      ? field.onChange
                      : (e: SelectChangeEvent<string>) => {
                          const str = e.target.value;
                          const hit = itemList.find(
                            (item) =>
                              item != null &&
                              item.key != null &&
                              String(item.key) === str,
                          );
                          field.onChange(hit ? hit.key : str);
                        }
                  }
                >
                  {!multiple ? (
                    <MenuItem value="" sx={{ display: "none" }} aria-hidden />
                  ) : null}
                  {itemList
                    .filter((item) => item != null && item.key != null)
                    .map((item) => {
                      return (
                        <MenuItem key={String(item.key)} value={String(item.key)}>
                          <ListItemText sx={{ textAlign: "left" }}>
                            {item.value}
                          </ListItemText>
                        </MenuItem>
                      );
                    })}
                </Select>
                {helperText ? (
                  <FormHelperText>{helperText}</FormHelperText>
                ) : null}
                {validationMessage ? (
                  <Typography variant="caption" color="error">
                    {validationMessage}
                  </Typography>
                ) : null}
              </FormControl>
            );
          }}
        />
      ) : (
        <FormControl
          fullWidth
          required={isRequired}
          key={String(defaultValue) + String(value)}
        >
          <InputLabel id={id}>
            {label}
          </InputLabel>
          <Select
            name={resolvedName}
            size="small"
            labelId={id + "label"}
            id={id + "field"}
            label={label}
            variant="outlined"
            sx={{ marginTop: "0px !important" }}
            defaultValue={defaultValue}
          >
            {itemList
              .filter((item) => item != null && item.key != null)
              .map((item) => {
                return (
                  <MenuItem key={String(item.key)} value={String(item.key)}>
                    <ListItemText sx={{ textAlign: "left" }}>
                      {item.value}
                    </ListItemText>
                  </MenuItem>
                );
              })}
          </Select>
          {helperText ? (
            <FormHelperText>{helperText}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    </>
  );
}

export default ControlledSelectField;
